// Keyboard-first navigation. Single keys are ignored while typing in a field;
// combos with ⌘/Ctrl always fire.
import { onBeforeUnmount, onMounted } from "vue";

export type HotkeyMap = Record<string, (e: KeyboardEvent) => void>;

function isTyping(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

// Keys look like "k", "mod+k", "shift+?", "escape".
export function comboOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("mod");
  if (e.altKey) parts.push("alt");
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
  if (e.shiftKey && e.key.length !== 1) parts.push("shift");
  parts.push(key);
  return parts.join("+");
}

export function useHotkeys(map: HotkeyMap): void {
  const handler = (e: KeyboardEvent) => {
    // keys inside an open dialog are the dialog's: Esc cancels it (2026-10-22, the shell's Esc swallowed it)
    if ((e.target as Element | null)?.closest?.("dialog[open]")) return;
    const combo = comboOf(e);
    const fn = map[combo];
    if (!fn) return;
    if (!combo.startsWith("mod+") && combo !== "escape" && isTyping(e)) return;
    e.preventDefault();
    fn(e);
  };
  onMounted(() => window.addEventListener("keydown", handler));
  onBeforeUnmount(() => window.removeEventListener("keydown", handler));
}
